/**
 * Strategy Builder API Service
 */

import { getConfigAsync } from '../utils/config';
import { getApiHeaders, getApiHeadersWithoutContentType } from '../utils/apiHeaders';
import {
  BacktestRequest,
  BacktestResponse,
  SaveStrategyRequest,
  SaveStrategyResponse,
  StrategiesListResponse,
  StrategyResponse,
  Strategy,
  BacktestResults,
  AIAnalysisRequest,
  AIAnalysisMessage
} from '../components/StrategyBuilder/types';

/**
 * Run a backtest for a strategy configuration
 */
export async function runBacktest(request: BacktestRequest): Promise<BacktestResults> {
  const config = await getConfigAsync();
  const API_BASE_URL = config.API_BASE_URL;
  
  console.log('[Strategy] Running backtest with request:', request);
  
  try {
    const response = await fetch(`${API_BASE_URL}/strategy/backtest`, {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(request)
    });
    
    const text = await response.text();
    console.log('[Strategy] Backtest response status:', response.status);
    console.log('[Strategy] Backtest response body:', text);
    
    let data: BacktestResponse;
    try {
      data = JSON.parse(text);
    } catch (parseError) {
      console.error('[Strategy] Failed to parse response as JSON:', parseError);
      throw new Error(`Server returned invalid response: ${text.substring(0, 200)}`);
    }
    
    if (!response.ok || !data.success) {
      console.error('[Strategy] Backtest failed:', data.error);
      throw new Error(data.error || 'Failed to run backtest');
    }
    
    console.log('[Strategy] Backtest successful, trades:', data.backtest_results?.total_trades);
    return data.backtest_results;
  } catch (error: any) {
    console.error('[Strategy] Backtest error:', error);
    throw error;
  }
}

/**
 * Save a strategy with its configuration and backtest results
 */
export async function saveStrategy(request: SaveStrategyRequest): Promise<string> {
  const config = await getConfigAsync();
  const API_BASE_URL = config.API_BASE_URL;
  
  const response = await fetch(`${API_BASE_URL}/strategy/save`, {
    method: 'POST',
    headers: getApiHeaders(),
    body: JSON.stringify(request)
  });
  
  const data: SaveStrategyResponse = await response.json();
  
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to save strategy');
  }
  
  return data.strategy_id;
}

/**
 * Get all strategies for the current user
 */
export async function getStrategies(): Promise<Strategy[]> {
  const config = await getConfigAsync();
  const API_BASE_URL = config.API_BASE_URL;
  
  const response = await fetch(`${API_BASE_URL}/strategy/list`, {
    method: 'GET',
    headers: getApiHeadersWithoutContentType()
  });
  
  const data: StrategiesListResponse = await response.json();
  
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to get strategies');
  }
  
  return data.strategies;
}

/**
 * Get a single strategy by ID
 */
export async function getStrategy(strategyId: string): Promise<Strategy> {
  const config = await getConfigAsync();
  const API_BASE_URL = config.API_BASE_URL;
  
  const response = await fetch(`${API_BASE_URL}/strategy/${strategyId}`, {
    method: 'GET',
    headers: getApiHeadersWithoutContentType()
  });
  
  const data: StrategyResponse = await response.json();
  
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Strategy not found');
  }
  
  return data.strategy;
}

/**
 * Update a strategy
 */
export async function updateStrategy(
  strategyId: string,
  updates: Partial<SaveStrategyRequest>
): Promise<void> {
  const config = await getConfigAsync();
  const API_BASE_URL = config.API_BASE_URL;
  
  const response = await fetch(`${API_BASE_URL}/strategy/${strategyId}`, {
    method: 'PUT',
    headers: getApiHeaders(),
    body: JSON.stringify(updates)
  });
  
  const data = await response.json();
  
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to update strategy');
  }
}

/**
 * Delete a strategy
 */
export async function deleteStrategy(strategyId: string): Promise<void> {
  const config = await getConfigAsync();
  const API_BASE_URL = config.API_BASE_URL;
  
  const response = await fetch(`${API_BASE_URL}/strategy/${strategyId}`, {
    method: 'DELETE',
    headers: getApiHeadersWithoutContentType()
  });
  
  const data = await response.json();
  
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to delete strategy');
  }
}

/**
 * Analyze a strategy using AI (streaming)
 * Returns an async generator that yields content chunks
 */
export async function* analyzeStrategy(
  request: AIAnalysisRequest
): AsyncGenerator<string, void, unknown> {
  const config = await getConfigAsync();
  const API_BASE_URL = config.API_BASE_URL;
  
  const response = await fetch(`${API_BASE_URL}/strategy/analyze`, {
    method: 'POST',
    headers: getApiHeaders(),
    body: JSON.stringify(request)
  });
  
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to analyze strategy');
  }
  
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }
  
  const decoder = new TextDecoder();
  let buffer = '';
  
  while (true) {
    const { done, value } = await reader.read();
    
    if (done) break;
    
    buffer += decoder.decode(value, { stream: true });
    
    // Process SSE data lines
    const lines = buffer.split('\n');
    buffer = lines.pop() || ''; // Keep incomplete line in buffer
    
    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const data = line.slice(6);
        
        if (data === '[DONE]') {
          return;
        }
        
        try {
          const parsed = JSON.parse(data);
          if (parsed.content) {
            yield parsed.content;
          }
          if (parsed.error) {
            throw new Error(parsed.error);
          }
        } catch (e) {
          // Ignore JSON parse errors for incomplete data
          if (data.trim() && !data.startsWith('{')) {
            console.warn('Unexpected SSE data:', data);
          }
        }
      }
    }
  }
}

/**
 * Get AI analysis suggestions
 */
export async function getAnalysisSuggestions(language: string = 'zh-CN'): Promise<string[]> {
  const config = await getConfigAsync();
  const API_BASE_URL = config.API_BASE_URL;
  
  const response = await fetch(`${API_BASE_URL}/strategy/analyze/suggestions?language=${language}`, {
    method: 'GET',
    headers: getApiHeadersWithoutContentType()
  });
  
  const data = await response.json();
  
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Failed to get suggestions');
  }
  
  return data.suggestions;
}

